import React from 'react';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './SellerHubPage.module.css';

const ICON_PATHS = {
  book:
    'M12 5.5C9.8 3.8 7 3.4 4 4.5v13c3-1.1 5.8-.7 8 1 2.2-1.7 5-2.1 8-1v-13c-3-1.1-5.8-.7-8 1Zm0 0v13',
  maple:
    'm12 3 1.7 4.2 3.8-1.1-1.7 3.5 3.2 1.8-3.8 1.1.8 4.2-3.9-1.7-.1 4.5h-2l-.1-4.5-3.9 1.7.8-4.2-3.8-1.1 3.2-1.8-1.7-3.5 3.8 1.1L12 3Z',
  leaf:
    'M19.5 4.5C11 4.4 5.2 8.5 5.2 14.1c0 3.2 2.3 5.4 5.6 5.4 5.8 0 8.5-6.2 8.7-15Zm-13 15c2.4-5 5.7-8.2 10.5-10.8',
  coins:
    'M7 7c0 1.7 2.2 3 5 3s5-1.3 5-3-2.2-3-5-3-5 1.3-5 3Zm0 0v4c0 1.7 2.2 3 5 3s5-1.3 5-3V7M3 11c0 1.7 2.2 3 5 3m-5-3v4c0 1.7 2.2 3 5 3 1.1 0 2.1-.2 2.9-.6',
  parcel: 'm12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm-8 4.5 8 4.5 8-4.5M12 12v9M8 5.2l8 4.6',
  bookmark: 'M6 4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21l-6-3.5L6 21V4.5Z',
  card:
    'M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11ZM4 8h16m-12 5h3',
  store: 'M4 9h16l-1.5-5h-13L4 9Zm1 0v10h14V9m-11 3v7m5-7v7',
  question:
    'M9.5 9a2.7 2.7 0 1 1 4.8 1.7c-1.1 1.1-2.3 1.5-2.3 3.1m0 3.3v.1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  mail: 'M4 6h16v12H4V6Zm1 1 7 5 7-5',
  people:
    'M16 19v-1.2c0-2-1.8-3.6-4-3.6s-4 1.6-4 3.6V19m8-8a2.5 2.5 0 1 0-5 0 2.5 2.5 0 0 0 5 0Zm2.5 8v-1c0-1.4-.8-2.6-2-3.2m-.5-6.3a2.2 2.2 0 0 1 0 4.3',
};

const Icon = ({ name }) => (
  <span className={css.iconCircle} aria-hidden="true">
    <svg viewBox="0 0 24 24" focusable="false">
      <path d={ICON_PATHS[name]} />
    </svg>
  </span>
);

const benefits = [
  ['book', 'Reader led', 'Built by book lovers, for book lovers.'],
  ['maple', 'Proudly Canadian', 'A marketplace made for Canadian readers.'],
  ['leaf', 'More books, less waste', 'Keep great books in circulation.'],
];

const guides = [
  [
    'book',
    'Seller Guide',
    'Everything you need to start selling, from your first listing to your first sale.',
    'selling',
  ],
  ['coins', 'Fees', 'Simple fees, explained clearly.', 'payments'],
  ['parcel', 'Shipping', 'How to pack, ship, and send it safely within Canada.', 'shipping'],
  ['bookmark', 'Condition Guide', 'Use the same standards buyers expect.', 'condition'],
  ['card', 'Getting Paid', 'How payouts work and the 48-hour hold.', 'getting-paid'],
  ['question', 'Seller FAQs', 'Quick answers for common selling questions.', 'seller-faqs'],
  [
    'store',
    'Manage Your Shop',
    'Set up your public PageHop Shop and seller profile.',
    'seller-shops',
  ],
];

/**
 * SellerHubPage is a dedicated seller onboarding and resources hub.
 * It explains the seller experience and provides access to key seller tools and guides
 * before users are asked to create a listing.
 */
const SellerHubPage = () => {
  return (
    <Page
      title="PageHop 101 | PageHop Books"
      description="Everything you need to buy, sell, ship, get paid, and make the most of PageHop."
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root}>
          <section className={css.hero}>
            <div className={css.heroCopy}>
              <h1>Everything you need to buy, sell, ship, and get paid on PageHop.</h1>
              <p className={css.heroText}>
                Your go-to guide for listing books, understanding fees, shipping confidently, and
                making the most of PageHop. Whether you’re a first-time seller or a seasoned bookish
                pro, we’ve got you.
              </p>
            </div>
            <div className={css.heroBenefits} aria-label="About PageHop">
              {benefits.map(([icon, title, copy]) => (
                <article className={css.benefitItem} key={title}>
                  <Icon name={icon} />
                  <div>
                    <h2>{title}</h2>
                    <p>{copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <div className={css.contentInner}>
            <section className={css.guides} aria-labelledby="guides-title">
              <h2 id="guides-title">Explore the Guides</h2>
              <p className={css.sectionIntro}>
                Quick answers, helpful tips, and everything you need to make the most of PageHop.
              </p>
              <div className={css.resourceGrid}>
                {guides.map(([icon, title, copy, topic]) => (
                  <NamedLink
                    className={css.resourceCard}
                    name="HowToUseShelfyTopicPage"
                    params={{ topic }}
                    key={title}
                  >
                    <Icon name={icon} />
                    <h3>{title}</h3>
                    <p>{copy}</p>
                    <span className={css.cardArrow} aria-hidden="true">
                      →
                    </span>
                  </NamedLink>
                ))}
              </div>
            </section>

            <section className={css.finalCtaSection}>
              <div>
                <h2>Ready to clear some shelf space?</h2>
                <p>Create your first listing and find its next reader.</p>
              </div>
              <NamedLink className={css.primaryCta} name="NewListingPage">
                Create a listing
              </NamedLink>
            </section>

            <section className={css.supportStrip} aria-label="Support links">
              <NamedLink
                className={css.supportItem}
                name="HowToUseShelfyTopicPage"
                params={{ topic: 'seller-faqs' }}
              >
                <Icon name="question" />
                <span>
                  <h2>Still have questions?</h2>
                  <p>Check out our Seller FAQs.</p>
                  <b>View FAQs →</b>
                </span>
              </NamedLink>
              <a className={css.supportItem} href="mailto:hello@shelfy.ca?subject=PageHop support">
                <Icon name="mail" />
                <span>
                  <h2>Need help?</h2>
                  <p>We’re here for you.</p>
                  <b>Contact Support →</b>
                </span>
              </a>
              <NamedLink
                className={css.supportItem}
                name="CMSPage"
                params={{ pageId: 'shelfy-social' }}
              >
                <Icon name="people" />
                <span>
                  <h2>Join the community</h2>
                  <p>Find tips, share experiences, and connect with other readers.</p>
                  <b>Visit the Community →</b>
                </span>
              </NamedLink>
            </section>
          </div>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default SellerHubPage;
